import React, { CSSProperties, PropsWithChildren, useEffect, useId, useState } from 'react';
import './style.scss';
import Icon from '@able/react/dist/Icon';
import { AbleCommonProps } from "../Types";

export type PopoverMenuProps = {
    /** Show or hide menu panel */
    isExpanded: boolean;
    /** useToggleDropdown's setToggleDropdown function to change state of isExpanded */
    setExpanded: (value: boolean) => void;
    getQueryText: (value: string) => void;
    className?: string;
    /** PopoverMenu variant */
    variant:
    'Default'
    | 'compact'
    | 'LowEmphasis'
    | 'LowEmphasisCompact'
    | 'MediumEmphasis'
    | 'MediumEmphasisCompact'
    | 'HighEmphasis'
    | 'HighEmphasisCompact'
    | 'EllipsisIcon'
    | 'EllipsisIconCompact';
    id?: string;
    /** Label shown on button */
    label: string;
    /** Object of event handlers
     */
    events?: Record<string, unknown>;
    /** Force position of menu above button*/
    forceMenuPositionAboveButton?: boolean;
    /** Force position of menu to right of button*/
    forceMenuAlignmentToButtonRight?: boolean;
    contentAriaLabel?: string;
    /** Z-Index for Menu Panel */
    zIndex?: number;
    /** Element ref to pass down targets button */
    buttonRef?: React.MutableRefObject<HTMLButtonElement | null>;
    /** Can pass function to run when Dropdown closes */
    onClose?: (button: HTMLButtonElement) => void;
    /** Can pass function to run when Dropdown opens */
    onOpen?: () => void;
    spacingType?: 'none' | 'compact' | 'default';
    multiSelectValues?: { "label": string, "value": string, "id": string, "name": string, "disabled"?: boolean, "checked"?: boolean }[];
    buttonAction?: string;
} & AbleCommonProps & PropsWithChildren;

/**
 * @deprecated
 *
 * This component has been deprecated, please use usePopover hook instead
 *
 * Before:
 *
 * `<PopoverMenu />`
 *
 * After:
 *
 * `usePopover` hook need to use to work with popover
 *
 * @deprecatedSince 1.0.47
 */


const PopoverMenu = ({
    className = '',
    developmentUrl = '',
    variant = 'MediumEmphasisCompact',
    id,
    label,
    events,
    forceMenuPositionAboveButton = false,
    forceMenuAlignmentToButtonRight = false,
    contentAriaLabel,
    children,
    zIndex = 2,
    isExpanded,
    setExpanded,
    buttonRef,
    onOpen,
    onClose,
}: PopoverMenuProps) => {
    const [menuWrapperClass, setMenuWrapperClass] = useState<string>();
    const [menuWrapperStyle, setMenuWrapperStyle] = useState<CSSProperties>({
        visibility: 'hidden',
    });

    const PopoverMenuRef = React.useRef<HTMLDivElement>(null);
    const btnRef = React.useRef<HTMLButtonElement | null>(null);
    const menuWrapperRef = React.useRef<HTMLDivElement>(null);
    const buttonClick = React.useRef(false);
    let timerRef: NodeJS.Timeout;

    const preFilledPopoverMenuId = useId();
    const popoverClassName = `tedui-popover--${variant} ${className}`;
    const PopoverMenuId = id ? id : preFilledPopoverMenuId;
    const buttonId = `${PopoverMenuId}-button`;
    const contentId = `${PopoverMenuId}-content`;
    const menuRef = PopoverMenuRef.current as HTMLDivElement;
    const PopoverMenuExpandedAboveClass = 'dropdown-menu--expanded-above';
    const PopoverMenuExpandedRightClass = 'dropdown-menu--expanded-right';

    const checkEllipsisIcon = (variant: string) => {
        if (variant === 'EllipsisIcon' || variant === 'EllipsisIconCompact') {
            return true;
        }
        return false;
    };

    const ariaLabelling = contentAriaLabel
        ? { 'aria-label': contentAriaLabel }
        : { 'aria-labelledby': buttonId };

    const { onClick } = events || {};

    const handleClick = () => {
        buttonClick.current = true;
        setExpanded(!isExpanded);
        if (onClick && typeof onClick === 'function') {
            onClick();
        }
    };

    const closeOnFocusOut = (event: FocusEvent) => {
        if (
            event &&
            menuRef && !menuRef.contains(event.relatedTarget as Node)
        ) {
            setExpanded(false);
        }
    };

    const closeOnEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            (btnRef.current as HTMLButtonElement).focus();
            setExpanded(false);
        }
    };

    const alignRight = (
        menuWrapper: HTMLDivElement,
        button: HTMLButtonElement,
        forceMenuAlignmentToButtonRight: boolean
    ) => {
        let menuStyleLeft: string | undefined, menuStyleRight: string | undefined;
        const screenWidth = document.body.clientWidth;
        const menuWrapperMargin = screenWidth * 0.15;
        const menuWrapperWidth = Math.min(screenWidth - menuWrapperMargin, 400);
        const menuWrapperMarginHalf = menuWrapperWidth < 400 ? menuWrapperMargin / 2 : 0;
        const menuWrapperPosition = menuWrapper.getBoundingClientRect();
        const menuWidth = Math.min(menuWrapperPosition.width, menuWrapperWidth);
        const menuRight = menuWrapperPosition.left + menuWidth;
        const buttonPosition = button.getBoundingClientRect();

        if (forceMenuAlignmentToButtonRight) {
            setMenuWrapperClass(PopoverMenuExpandedRightClass);
        } else if (Math.floor(menuRight) >= screenWidth) {
            setMenuWrapperClass(PopoverMenuExpandedRightClass);
            menuStyleLeft = undefined;
            if (Math.floor(buttonPosition.right - menuWidth) <= 0) {
                menuStyleRight =
                    Math.floor(buttonPosition.right - menuWidth - menuWrapperMarginHalf) + 'px';
            }
            if (buttonPosition.right + menuWrapperMarginHalf >= screenWidth) {
                menuStyleRight = `${menuWrapperMarginHalf + buttonPosition.right - screenWidth}px`;
            }
        } else if (buttonPosition.left + menuWidth <= screenWidth) {
            menuStyleRight = undefined;
            setMenuWrapperClass(undefined);
            if (buttonPosition.left <= menuWrapperMarginHalf) {
                menuStyleLeft = `${menuWrapperMarginHalf - buttonPosition.left}px`;
            }
        } else if (
            menuWrapper.classList.contains(PopoverMenuExpandedRightClass) &&
            Math.floor(buttonPosition.right - menuWidth - menuWrapperMarginHalf) <= 0
        ) {
            menuStyleRight =
                Math.floor(buttonPosition.right - menuWidth - menuWrapperMarginHalf) + 'px';
        } else {
            menuStyleRight = '0px';
        }
        setMenuWrapperStyle({
            maxWidth: `${menuWrapperWidth}px`,
            left: menuStyleLeft,
            right: menuStyleRight,
            visibility: 'visible',
        });
    };

    const alignTop = (
        menuWrapper: HTMLDivElement,
        button: HTMLButtonElement,
        forceMenuPositionAboveButton: boolean
    ) => {
        const menuWrapperPosition = menuWrapper.getBoundingClientRect();
        const buttonPosition = button.getBoundingClientRect();
        const bottomOfMenuDiv = menuWrapperPosition.bottom;
        if (
            bottomOfMenuDiv > window.innerHeight ||
            menuWrapper.style.bottom ||
            forceMenuPositionAboveButton
        ) {
            const shouldBeAbove = buttonPosition.top >= menuWrapperPosition.height;
            const newBottomPosition = buttonPosition.height + 16;
            if (forceMenuPositionAboveButton || shouldBeAbove) {
                setMenuWrapperClass(PopoverMenuExpandedAboveClass);
                setMenuWrapperStyle((e) => ({ ...e, bottom: `${newBottomPosition}px` }));
            }
        }
    };

    const PositionAdjust = (
        menuWrapper: HTMLDivElement,
        button: HTMLButtonElement,
        forceMenuAlignmentToButtonRight: boolean,
        forceMenuPositionAboveButton: boolean
    ) => {
        alignRight(menuWrapper, button, forceMenuAlignmentToButtonRight);
        alignTop(menuWrapper, button, forceMenuPositionAboveButton);
    };

    const adjustPositionRightAbove = () => {
        PositionAdjust(
            menuWrapperRef.current as HTMLDivElement,
            btnRef.current as HTMLButtonElement,
            forceMenuAlignmentToButtonRight,
            forceMenuPositionAboveButton
        );
    };

    const debounceResize = () => {
        if (timerRef !== null) {
            clearTimeout(timerRef);
        }
        timerRef = setTimeout(() => {
            adjustPositionRightAbove();
        }, 100);
    };

    const closeCleanUp = () => {
        setMenuWrapperClass(undefined);
        setMenuWrapperStyle({ visibility: 'hidden' });
    };

    useEffect(() => {
        (PopoverMenuRef.current as HTMLDivElement).addEventListener('focusout', closeOnFocusOut);
        (PopoverMenuRef.current as HTMLDivElement).addEventListener('keydown', closeOnEscapeKey);
        if (isExpanded) {
            adjustPositionRightAbove();
            window.addEventListener('resize', debounceResize);
            if (onOpen && typeof onOpen === 'function') onOpen();
            buttonClick.current = true;

        } else {
            closeCleanUp();
            if (onClose && typeof onClose === 'function')
                buttonClick.current && onClose(btnRef.current as HTMLButtonElement);
            buttonClick.current = false;
        }
        return () => {
            window.removeEventListener('resize', debounceResize);
            PopoverMenuRef.current?.removeEventListener('focusout', closeOnFocusOut);
            PopoverMenuRef.current?.removeEventListener('keydown', closeOnEscapeKey);
        };
    }, [isExpanded]);

    return (
        <div className={popoverClassName} id={PopoverMenuId} ref={PopoverMenuRef}>
            <button
                className="popover-btn"
                type="button"
                data-testid={"popoverbtn"}
                aria-expanded={isExpanded}
                aria-controls={contentId}
                id={buttonId}
                {...events}
                onClick={handleClick}
                onKeyUp={() => { return true; }}
                ref={(e) => {
                    btnRef.current = e;
                    if (buttonRef) buttonRef.current = e;
                }}
            >
                {!checkEllipsisIcon(variant) ? label : ''}
                {!checkEllipsisIcon(variant) ? (
                    <Icon icon="ChevronDown" developmentUrl={developmentUrl} size="24" />
                ) : (
                    <Icon
                        icon="More"
                        developmentUrl={developmentUrl}
                        screenReaderContent={label}
                        size="24"
                    />
                )}
            </button>
            <div
                className={menuWrapperClass}
                id={contentId}
                {...ariaLabelling}
                ref={menuWrapperRef}
                role="region"
                tabIndex={-1}
                style={{ ...menuWrapperStyle, zIndex: zIndex }}
            >
                {isExpanded && children}
            </div>
        </div>
    );
};

export default PopoverMenu;